/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 1/7/21.
 */

import {createStore, createLogger} from "vuex";
import Asset from "./modules/Asset";
import AssetManagement from "./modules/AssetManagement";
import Event from "./modules/Event";
import HotKeys from "./modules/HotKeys";
import Jobs from "./modules/Jobs";
import Library from "./modules/Library";
import Notifications from "./modules/Notifications";
import Player from "./modules/Player";
import Playlist from "./modules/Playlist";
import Scheduler from "./modules/Scheduler";
import Sequencer from "./modules/Sequencer";
import Station from "./modules/Station";
import StudioSettings from "./modules/StudioSettings";
import User from "./modules/User";
import WebRTC from "./modules/WebRTC";
import Websocket from "./modules/Websocket";

import sharedStorePlugin from "./plugins/sharedStore";

import config from "@/config";
import {setCookie, getCookie} from "@/plugins/cookies";
import WorkerTimer from "@/plugins/WorkerTimer";

import PortalApi from "@/services/Portal";
import SessionManagerLoader from "@/services/SessionManagerLoader";

let clientId = new URLSearchParams(location.search).get("client");
if (clientId) {
	//In the case of MidiUI, remove the clientId query param from the url
	history.replaceState({}, null, location.href.split("?")[0]);
} else {
	//In the case of Live Console UI, generate a random clientId
	clientId = Math.random().toString(16).substring(2);
}

export default createStore({
	modules: {
		Asset,
		AssetManagement,
		Event,
		HotKeys,
		Jobs,
		Library,
		Notifications,
		Player,
		Playlist,
		Scheduler,
		Sequencer,
		Station,
		StudioSettings,
		User,
		WebRTC,
		Websocket
	},

	state: {
		clientId: clientId,

		authConfig: {
			auth: {
				authority: `${config.AUTHORITY_BASE_URL}/${config.AUTHORITY_ORG_ID}`,
				clientId: config.AUTHORITY_CLIENT_ID,
				redirectUri: "/auth/callback",
				postLogoutRedirectUri: window.location.href
			},
			cache: {
				cacheLocation: "localStorage",
				storeAuthStateInCookie: false
			},
			system: {
				allowRedirectInIframe: true
			}
		},

		//todo: move it from here
		account: null,

		authAccount: null,

		accessToken: null,

		containerRefs: {},

		liveMode: getCookie("live-mode"),

		darkMode: getCookie("dark-mode"),

		systemInputDevices: null,

		systemOutputDevices: null
	},

	getters: {
		clientId: state => () => {
			return state.clientId;
		},

		getAuthAccount: state => () => {
			return state.authAccount;
		},

		getAccessToken: state => () => {
			return state.accessToken;
		},

		getUserID: state => () => {
			return state.authAccount?.userId;
		},

		getUserEmail: state => () => {
			return state.authAccount?.email;
		},

		getUserFullName: state => () => {
			return state.authAccount?.fullName;
		},

		getOrgID: state => () => {
			return state.authAccount?.organizationId;
		},

		getContainerByRef: state => ref => {
			return state.containerRefs[ref];
		},

		liveMode: state => () => {
			return /true/.test(state.liveMode);
		},

		darkMode: (state, getters) => () => {
			return getters.getStudioSettings()?.preferences?.darkMode ?? /true/.test(state.darkMode);
		},

		systemInputDevices: state => () => {
			return state.systemInputDevices;
		},

		systemOutputDevices: state => () => {
			return state.systemOutputDevices;
		},

		//todo: move it from here
		getAccount: state => () => {
			return state.account;
		}
	},

	mutations: {
		setAuthAccount(state, account) {
			state.authAccount = account;
		},

		setAccessToken(state, token) {
			if (token) {
				state.accessToken = `Bearer ${token}`;
			} else {
				state.accessToken = null;
			}
		},

		setContainerByRef(state, {ref, component}) {
			state.containerRefs[ref] = component;
		},

		unsetContainerByRef(state, ref) {
			delete state.containerRefs[ref];
		},

		setLiveMode(state, enabled) {
			setCookie("live-mode", enabled, 3650);
			state.liveMode = enabled;
		},

		setDarkMode(state, enabled) {
			setCookie("dark-mode", enabled, 3650);
			state.darkMode = enabled;
		},

		setSystemInputDevices(state, devices) {
			state.systemInputDevices = devices;
		},

		setSystemOutputDevices(state, devices) {
			state.systemOutputDevices = devices;
		},

		//todo: move it from here
		setAccount(state, account) {
			state.account = account;
		},

		resetAccountState(state) {
			state.authAccount = null;
			state.accessToken = null;
			state.account = null;
		}
	},

	actions: {
		//todo: move it from here
		async getAccount({getters}) {
			return await PortalApi.getAccount(getters.getOrgID()).then(response => response.data);
		},

		async getOrganizationsByID(state, orgIDList) {
			let orgPromises = [];
			orgIDList.forEach(orgID => orgPromises.push(PortalApi.getAccount(orgID)));

			return Promise.allSettled(orgPromises).then(responses =>
				responses.filter(r => r.status === "fulfilled").map(r => r.value.data)
			);
		},

		async signOut({dispatch}, inactivity) {
			const SessionManager = await SessionManagerLoader();
			SessionManager.SignOut(inactivity).then(() => {
				//todo: Temporary solution. Disable logout process for inactivity reason, since the RCS IAM doesn't work properly
				dispatch("resetState");
				dispatch("closeWebsocket");
			});
		},

		resetState({commit}, changeAccount = false) {
			if (changeAccount) {
				commit("resetAccountState");
				commit("resetUserObjects");
				commit("resetStationState");
			} else {
				commit("resetLoadedStations");
				commit("resetLiveModeSelectedStationID");
			}
			commit("resetPlayerState");
			commit("resetSequencerState");
			commit("resetSchedulerState");
			commit("resetAssetsHotkeysSubscriptions");
			commit("resetPlaylistState");
			commit("resetHotkeysPages");
			WorkerTimer.clearAll();
		},

		async getSystemDevices({commit, dispatch}) {
			try {
				await navigator.mediaDevices.getUserMedia({audio: true, video: false});
				const allDevices = await navigator.mediaDevices.enumerateDevices();
				const systemInputDevices = allDevices
					.filter(d => d.kind === "audioinput")
					.map(d => {
						return {device: d.deviceId, name: d.label};
					});
				const systemOutputDevices = allDevices
					.filter(d => d.kind === "audiooutput")
					.map(d => {
						return {device: d.deviceId, name: d.label};
					});

				commit("setSystemInputDevices", systemInputDevices);
				commit("setSystemOutputDevices", systemOutputDevices);
				dispatch("setActiveStudioDevices");
			} catch (error) {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message:
						"You'll need to allow microphone access in order to have access to the PC Audio out devices."
				});
				commit("setSystemInputDevices", []);
				commit("setSystemOutputDevices", []);
				dispatch("setActiveStudioDevices");
			}
		}
	},

	plugins: [
		...(config.ENV !== "production" ? [createLogger({logActions: false, logMutations: false})] : []),
		sharedStorePlugin({
			mutations: [
				"setAccessToken",
				"setAuthAccount",
				"setLiveModeSelectedStationID",
				"setStudioSettings",
				"setSequencer",
				"setLivePlayerConferenceSnapshot",
				"setHotKeysRowsPerSoundCard",
				"setAssetTypes",
				"addAssetHotkeySubscription",
				"setMicOnOff",
				"setUserMicState",
				"setUserAirState",
				"setActiveStudioHotkeysOutputDevices",
				"setDarkMode"
			],
			// State paths that should be snapshotted and handed over to a newly
			// opened window (e.g. MidiUI) so that it does not start with an empty
			// store while missing the mutations that happened before it opened.
			state: [
				"authAccount",
				"accessToken",
				"darkMode",
				"StudioSettings.studioSettings",
				"StudioSettings.activeStudioHotkeysOutputDevices",
				"Sequencer.sequencer",
				"Websocket.sequencerModeStatus",
				"Station.liveModeSelectedStationID",
				"Station.liveConferenceSnapshot",
				"HotKeys.selectedPage.hotKeysRowsPerSoundCard",
				"Asset.assetTypes",
				"Asset.assetsHotkeysSubscriptionsMap",
				"WebRTC.micOn",
				"WebRTC.usersMicState",
				"WebRTC.usersAirState"
			],
			clientId
		})
	]
});
